import React from 'react';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';

import { RadioGroup } from 'shared/components';
import { isRecMed } from 'shared/core/helpers/dispensaries';
import useTranslation from 'hooks/use-translation';
import useCart from 'hooks/use-cart';
import useSwitchMenuType from 'src/hooks/use-switch-menu-type';
import { FormTextInput, FormPhoneNumberInput, FormDateInput } from './form-input';
import { Header, InputContainer, InputLabel, Star } from './components';

export default function OrderInformation(props) {
  const { dispensary } = props;
  const { t } = useTranslation();
  const phoneIsRequired = dispensary?.orderTypesConfigV2.kiosk?.phoneRequired;
  const fullNameOnly = dispensary?.orderTypesConfigV2.kiosk?.fullNameOnly;
  const shouldShowBirthdateField = dispensary?.orderTypesConfigV2.kiosk?.showBirthdateField;
  const shouldShowNotesField = dispensary?.orderTypesConfigV2.kiosk?.notesField;
  const hidePhoneField = dispensary?.orderTypesConfigV2.kiosk?.hidePhoneField;
  const hideEmailField = dispensary?.orderTypesConfigV2.kiosk?.hideEmailField;
  const cart = useCart();
  const orderType = useObserver(() => (cart.isMedical ? 'med' : 'rec'));
  const switchMenuType = useSwitchMenuType();

  const fullNameText = t('order-information.full-name', 'Full Name');
  const firstNameText = t('order-information.first-name', 'First Name');
  const lastNameText = t('order-information.last-name', 'Last Name');

  return (
    <Container>
      <HeaderLine>
        <Header>Order Information</Header>
        <Required>*Required</Required>
      </HeaderLine>
      <InputContainer>
        <InputLabel>
          Name:<Star>*</Star>
        </InputLabel>
        <NameBox>
          <FormTextInput required name='firstName' label={fullNameOnly ? fullNameText : firstNameText} />
          {!fullNameOnly && <FormTextInput required label={lastNameText} name='lastName' />}
        </NameBox>
      </InputContainer>
      {!hidePhoneField && (
        <InputContainer>
          <InputLabel>Phone:{phoneIsRequired && <Star>*</Star>}</InputLabel>
          <FormPhoneNumberInput required={phoneIsRequired} name='phone' />
        </InputContainer>
      )}
      {!hideEmailField && (
        <InputContainer>
          <InputLabel>Email:</InputLabel>
          <FormTextInput label='Email address' name='email' />
        </InputContainer>
      )}
      {shouldShowBirthdateField && (
        <InputContainer>
          <InputLabel>
            Birthday:<Star>*</Star>
          </InputLabel>
          <FormDateInput required name='birth' />
        </InputContainer>
      )}
      {shouldShowNotesField && (
        <InputContainer>
          <InputLabel>Notes:</InputLabel>
          <FormTextInput name='notes' id='specialInstructions' />
        </InputContainer>
      )}
      {isRecMed(dispensary) && (
        <InputContainer>
          <InputLabel>
            Order Type:<Star>*</Star>
          </InputLabel>

          <RadioGroup
            options={[
              {
                label: 'Recreational',
                value: 'rec',
              },
              {
                label: 'Medical',
                value: 'med',
              },
            ]}
            display='inline-block'
            defaultValue={orderType}
            onChange={switchMenuType}
          />
        </InputContainer>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 0 45px;
`;

const HeaderLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  & + div {
    margin-top: 0px;
  }
`;

const Required = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[60]};
  margin-bottom: 30px;
`;

const NameBox = styled.div`
  display: flex;
  width: 100%;

  && > * {
    margin-right: 15px;

    &:last-child {
      margin-right: 0px;
    }
  }
`;
